// Brush preview for the Paint Tool panel: the production GPU engine paints a
// sample stroke on a small temporary surface with the current settings
// (brush, size, medium, pigments, loading, water, pressure response, paper
// and lighting). Previews are debounced by the caller, cached by settings,
// cancelled when superseded, and wait while the user is painting.

import { createGpuPaintEngine } from '../gpu/WebGPUPaintEngine.js';
import { paperSpectrum } from '../pigments/PigmentLibrary.js';
import { resolveSurfaceSettings } from '../surfaces/SurfaceLibrary.js';
import { toolFromPaintSettings, environmentFromPaintSettings, paintSampleFromPoint } from './PhysicalPaintSystem.js';
import { paintActivity } from './paintActivity.js';

// Simulation cells of the preview surface (rendered at 2x).
export const PREVIEW_CELLS = Object.freeze({ width: 160, height: 48 });
// Largest brush radius (cells) drawn at true scale; larger brushes are scaled down.
export const PREVIEW_MAX_RADIUS = 14;
const CACHE_LIMIT = 32;

const cache = new Map();
// Renders in progress, by key (two requests for the same preview share one).
const pending = new Map();
// Latest key each requester (one preview component) asked for. A render is
// skipped only when no requester wants it any more.
const latestByRequester = new Map();
const wanted = (key) => latestByRequester.size === 0 || [...latestByRequester.values()].includes(key);
let queue = Promise.resolve();

// Called when a preview component unmounts.
export function releaseBrushPreviewRequester(requester) {
  latestByRequester.delete(requester);
}

export function previewKey(settings = {}, surface = {}) {
  const p = settings.physical || {};
  return JSON.stringify([
    settings.tool, settings.paintColor, settings.toolSize, settings.brushPressure, settings.lightAngle, settings.lightElevation, settings.gloss,
    p.medium, p.brush, p.pigment, p.secondPigment, p.mix, p.loading, p.waterScale, p.thicknessScale, p.pigmentScale,
    p.fluidityScale, p.dryingScale, p.pickupScale, p.rotationMode, p.humidity, p.impastoDepth,
    surface.preset, surface.paperColor, surface.mode, surface.physicalStrength, surface.relief, surface.visibility,
  ]);
}

// Brush radius in preview cells and the scale it is shown at (1 = true size).
export function previewScale(settings = {}) {
  const radius = Math.max(1, ((Number(settings.toolSize) || 70) / 2) * 0.5);
  const scale = radius > PREVIEW_MAX_RADIUS ? PREVIEW_MAX_RADIUS / radius : 1;
  return { radius: radius * scale, scale };
}

// Sample stroke in document-like points: an S-curve with a pressure swell.
function samplePoints(width, height) {
  const points = [];
  const n = 48;
  for (let i = 0; i <= n; i += 1) {
    const u = i / n;
    points.push({
      x: (14 + u * (width - 28)) * 2,
      y: (height / 2 + Math.sin(u * Math.PI * 2) * height * 0.16) * 2,
      inputPressure: 0.25 + 0.6 * Math.sin(Math.min(1, u * 1.1) * Math.PI),
      pointerType: 'pen',
      time: 1000 + i * 16,
    });
  }
  return points;
}

const waitUntilIdle = async () => {
  // Painting has priority: wait until no stroke is in progress.
  for (let i = 0; i < 200 && (paintActivity.active || performance.now() - paintActivity.lastStroke < 250); i += 1) {
    await new Promise((r) => setTimeout(r, 120));
  }
};

async function render(gpu, settings, surface) {
  const { width, height } = PREVIEW_CELLS;
  const preset = resolveSurfaceSettings(surface.preset || 'cold_press', surface);
  const engine = createGpuPaintEngine(gpu, {
    width,
    height,
    surface: surface.preset || 'cold_press',
    surfaceOverrides: surface,
    seed: 7,
    substrate: paperSpectrum(preset.tone ?? 0.86, preset.warmth ?? 0.04),
  });
  try {
    const { radius } = previewScale(settings);
    engine.setEnvironment(environmentFromPaintSettings(settings));
    const tool = { ...toolFromPaintSettings(settings, 0.5, 'preview'), radius };
    const points = samplePoints(width, height);
    if (tool.brush === 'palette_knife') {
      // A knife needs paint to work: lay a band with the same paint first.
      const band = { ...tool, brush: 'flat_hog', radius: Math.min(radius, height * 0.3) };
      engine.beginStroke(band, { x: 10, y: height / 2, pressure: 0.7, time: 0 });
      for (let x = 12; x <= width - 10; x += 2) engine.strokeTo({ x, y: height / 2, pressure: 0.7, time: x * 4 });
      engine.endStroke();
    }
    const state = { travelled: 0, last: null, pointerType: '' };
    const samples = points.map((p) => paintSampleFromPoint(p, settings, state, 0.5, p.time));
    engine.beginStroke(tool, samples[0]);
    for (const s of samples.slice(1)) engine.strokeTo(s);
    engine.endStroke();
    engine.simulate(0.8);
    const canvas = new OffscreenCanvas(width * 2, height * 2);
    const context = canvas.getContext('webgpu');
    context.configure({ device: gpu.device, format: 'rgba8unorm', alphaMode: 'opaque' });
    const light = {
      azimuth: Number.isFinite(Number(settings.lightAngle)) ? Number(settings.lightAngle) : 315,
      elevation: Number.isFinite(Number(settings.lightElevation)) ? Number(settings.lightElevation) : 35,
      specular: Number.isFinite(Number(settings.gloss)) ? 0.2 + (Number(settings.gloss) / 100) * 0.8 : 0.6,
      depthScale: Number(settings.physical?.impastoDepth) || 1,
    };
    engine.renderToView(context.getCurrentTexture().createView(), 'rgba8unorm', light, 0);
    await gpu.device.queue.onSubmittedWorkDone();
    return canvas.transferToImageBitmap();
  } finally {
    engine.destroy();
  }
}

// Resolves to an ImageBitmap (shared, do not close), or null when the
// requester has asked for a different preview in the meantime. requester is
// any object identifying the caller (one per preview component).
export function requestBrushPreview(gpu, settings, surface = {}, requester = null) {
  const key = previewKey(settings, surface);
  if (requester) latestByRequester.set(requester, key);
  const current = () => !requester || latestByRequester.get(requester) === key;
  if (cache.has(key)) {
    const bitmap = cache.get(key);
    cache.delete(key);
    cache.set(key, bitmap);
    return Promise.resolve(bitmap);
  }
  if (pending.has(key)) return pending.get(key).then((bitmap) => (current() ? bitmap : null));
  // One preview at a time, in request order.
  const render$ = queue.then(async () => {
    if (!wanted(key)) return null;
    await waitUntilIdle();
    if (!wanted(key)) return null;
    const bitmap = await render(gpu, settings, surface);
    cache.set(key, bitmap);
    while (cache.size > CACHE_LIMIT) {
      const oldest = cache.keys().next().value;
      cache.get(oldest).close();
      cache.delete(oldest);
    }
    return bitmap;
  }).finally(() => pending.delete(key));
  pending.set(key, render$);
  queue = render$.catch(() => null);
  return render$.then((bitmap) => (current() ? bitmap : null));
}
